import { useCallback } from 'react';
import { useNavigate } from '../../../../../src/hooks/useI18nNavigate';
import { 
  profile_replyInProgress,
  profile_replyError
} from '@paraglide/messages';
import { ProfileTabType, PAGE_SIZE } from '../base';

interface UseProfileHandlersProps {
  profile: any;
  activeTab: ProfileTabType;
  isAuthenticated: boolean;
  refreshTweets?: () => void;
  showToast?: (message: string, type: 'success' | 'error') => void;
  onTabChange?: (tab: ProfileTabType) => void;
  setActiveTab: (tab: ProfileTabType) => void;
  setShowComposeModal: (show: boolean) => void;
  setReplyToTweet: (tweet: any) => void;
  tweets: any[];
  userProjects: any[];
  setUserProjects: (projects: any[]) => void;
  setProjectsPage: (page: number) => void;
  setHasMoreProjects: (hasMore: boolean) => void;
  fetchUserProjects: (userId: string, page: number, pageSize: number) => void;
  userCourses: any[];
  setUserCourses: (courses: any[]) => void;
  setCoursesPage: (page: number) => void;
  setHasMoreCourses: (hasMore: boolean) => void;
  fetchUserCourses: (userId: string, page: number, pageSize: number) => void;
  userRoadmaps: any[];
  setUserRoadmaps: (roadmaps: any[]) => void;
  setRoadmapsPage: (page: number) => void;
  setHasMoreRoadmaps: (hasMore: boolean) => void;
  fetchUserRoadmaps: (userId: string, page: number, pageSize: number) => void;
}

export const useProfileHandlers = ({
  profile,
  activeTab,
  isAuthenticated,
  refreshTweets,
  showToast,
  onTabChange,
  setActiveTab,
  setShowComposeModal,
  setReplyToTweet,
  tweets,
  userProjects,
  setUserProjects,
  setProjectsPage,
  setHasMoreProjects,
  fetchUserProjects,
  userCourses,
  setUserCourses,
  setCoursesPage,
  setHasMoreCourses,
  fetchUserCourses,
  userRoadmaps,
  setUserRoadmaps,
  setRoadmapsPage,
  setHasMoreRoadmaps,
  fetchUserRoadmaps
}: UseProfileHandlersProps) => {
  const navigate = useNavigate();

  const profileData = profile?.basicInfo ? profile : profile?.data ?? profile;

  const handleTabClick = useCallback((tabId: string) => {
    const profileTab = tabId as ProfileTabType;
    const targetUserId = profileData?.basicInfo?.id;
    setActiveTab(profileTab);

    if (profileTab === 'projects' && targetUserId && ((userProjects || []).length === 0 || activeTab !== 'projects')) {
      setUserProjects([]);
      setProjectsPage(0);
      setHasMoreProjects(true);
      fetchUserProjects(String(targetUserId), 0, PAGE_SIZE);
    } else if (profileTab === 'learning' && targetUserId && ((userCourses || []).length === 0 || activeTab !== 'learning')) {
      setUserCourses([]);
      setCoursesPage(0);
      setHasMoreCourses(true);
      fetchUserCourses(String(targetUserId), 0, PAGE_SIZE);
    } else if (profileTab === 'roadmaps' && targetUserId && ((userRoadmaps || []).length === 0 || activeTab !== 'roadmaps')) {
      setUserRoadmaps([]);
      setRoadmapsPage(0);
      setHasMoreRoadmaps(true);
      fetchUserRoadmaps(String(targetUserId), 0, PAGE_SIZE);
    }
    else if (profileTab === 'tweets' && activeTab === 'tweets') {
      if (refreshTweets) refreshTweets();
    }

    if (onTabChange) onTabChange(profileTab);
  }, [
    profileData?.basicInfo?.id,
    activeTab,
    userProjects?.length,
    userCourses?.length,
    userRoadmaps?.length,
    onTabChange,
    setActiveTab,
    setUserProjects,
    setProjectsPage,
    setHasMoreProjects,
    fetchUserProjects,
    setUserCourses,
    setCoursesPage,
    setHasMoreCourses,
    fetchUserCourses,
    setUserRoadmaps,
    setRoadmapsPage,
    setHasMoreRoadmaps,
    fetchUserRoadmaps,
    refreshTweets
  ]);

  const handleComment = useCallback((id: string) => {
    if (!isAuthenticated) { 
      navigate('/landing'); 
      return; 
    }
    const targetTweet = (tweets || []).find(tweet => String(tweet.id) === String(id));
    if (targetTweet) { 
      setReplyToTweet(targetTweet); 
      setShowComposeModal(true); 
    }
  }, [isAuthenticated, tweets, navigate, setReplyToTweet, setShowComposeModal]);

  const handleTweetSubmit = useCallback(async (data: any) => {
    try {
      const event = new CustomEvent('submit-tweet', { detail: { tweetData: data } });
      window.dispatchEvent(event);
      setShowComposeModal(false); 
      setReplyToTweet(null);
      setTimeout(() => { if (refreshTweets) refreshTweets(); }, 1000);
      if (showToast) showToast(profile_replyInProgress(), 'success');
      return true;
    } catch (error) {
      if (showToast) showToast(profile_replyError(), 'error');
      return false;
    }
  }, [setShowComposeModal, setReplyToTweet, refreshTweets, showToast]);

  const handleCloseModal = useCallback(() => { 
    setShowComposeModal(false); 
    setReplyToTweet(null); 
  }, [setShowComposeModal, setReplyToTweet]);

  // 프로젝트 관련 핸들러들
  const handleEditProject = useCallback((id: string) => {
    console.log(`Edit project: ${id}`);
  }, []);

  const handleDeleteProject = useCallback((id: string) => {
    console.log(`Delete project: ${id}`);
  }, []);

  const handleDuplicateProject = useCallback((id: string) => {
    console.log(`Duplicate project: ${id}`);
  }, []);

  const handleShareProject = useCallback((id: string) => {
    console.log(`Share project: ${id}`);
  }, []);

  const handleReportProject = useCallback((id: string) => {
    console.log(`Report project: ${id}`);
  }, []);

  const handleViewProjectDetails = useCallback((id: string) => {
    navigate(`/project/${id}`);
  }, [navigate]);

  // 로드맵 관련 핸들러들
  const handleViewRoadmapDetails = useCallback((id: string) => {
    navigate(`/learn/roadmap/${id}`);
  }, [navigate]);

  const handleEditRoadmap = useCallback((id: string) => {
    console.log(`Edit roadmap: ${id}`);
  }, []);

  const handleDeleteRoadmap = useCallback((id: string) => {
    console.log(`Delete roadmap: ${id}`);
  }, []);

  return {
    handleTabClick,
    handleComment,
    handleTweetSubmit,
    handleCloseModal,
    handleEditProject,
    handleDeleteProject,
    handleDuplicateProject,
    handleShareProject,
    handleReportProject,
    handleViewProjectDetails,
    handleViewRoadmapDetails,
    handleEditRoadmap,
    handleDeleteRoadmap
  };
};
